import { useEffect, useMemo, useRef, useState } from 'react';
import { api, getToken } from '../lib/api';
import { useAuth } from '../auth/AuthContext';
import { avisar, textoDeError } from '../components/Dialogo';

// Bytes de la foto POR EL MISMO ORIGEN (con el token en el header). Directo al
// blob de Azure no se puede: <a download> cross-origin abre pestaña, y el
// canvas del editor quedaría "manchado" (CORS) sin poder exportar.
async function fetchArchivo(id: string): Promise<Blob> {
  const res = await fetch(`/api/marketing/galeria/archivo/${id}`, {
    headers: getToken() ? { 'x-auth-token': getToken() as string } : undefined,
  });
  if (!res.ok) throw new Error(`No se pudo obtener la foto (${res.status})`);
  return res.blob();
}

async function descargar(it: Item) {
  const blob = await fetchArchivo(it.id);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = (it.titulo.replace(/[^\w\-. ]+/g, '') || 'foto') + '.jpg';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

/**
 * Galería — la mesa de trabajo de Marketing.
 *
 * VE TODO el material del expediente (pedido textual) para poder planear, pero
 * la pantalla no deja confundir "lo veo" con "lo puedo publicar": cada foto
 * trae su bandera de autorización y la advertencia va arriba, en grande.
 * Sin nombres de pacientes: la referencia es el código (8 del ID).
 *
 * Trae su propio EDITOR (pluma, flecha, círculo, texto) que guarda una COPIA
 * "(marketing)" en el mismo expediente — la original nunca se toca.
 */

interface CollageItem { document_id: string; x: number; y: number; s: number; r: number }
interface Item {
  id: string; titulo: string; esAD: boolean; autorizada: boolean; paciente: string;
  tratamientos: string[]; fecha: string; url: string;
  mime?: string | null; nombre?: string | null; formato?: string;
  // esMarketing = material creado/subido por marketing (privado por default);
  // compartida = también visible para los médicos en DraIA; oculta = papelera.
  esMarketing: boolean; compartida: boolean; oculta: boolean;
  collage: { layout?: string; cellH?: number; items?: CollageItem[] } | null;
}
interface Resp {
  sinDatos?: boolean; motivo?: string; queHacer?: string;
  items: Item[]; total: number; autorizadas: number; conAD: number; ocultas: number; truncado: boolean; advertencia: string;
  // Fotos RAW (DNG/ProRAW) que el servidor convirtió a JPEG en esta carga, las
  // que no pudo y cuántas faltan. Cero en todo = no hay nada que arreglar.
  rawConvertidas?: number; rawFallidas?: RawFallida[]; rawPendientes?: number;
}
interface RawFallida { id: string; nombre: string | null; motivo: string }

// El servidor convierte un lote chico por carga; aquí sólo se dice qué pasó y
// cuántas faltan, para que la persona sepa si toca volver a abrir o apretar.
function mensajeRaw(r: { rawConvertidas?: number; rawFallidas?: RawFallida[]; rawPendientes?: number }): string | null {
  const c = r.rawConvertidas || 0, f = r.rawFallidas || [], p = r.rawPendientes || 0;
  if (!c && !f.length && !p) return null;
  const partes: string[] = [];
  if (c) partes.push(`${c} foto${c === 1 ? '' : 's'} RAW convertida${c === 1 ? '' : 's'} a JPEG`);
  if (f.length) partes.push(`${f.length} no se pudo${f.length === 1 ? '' : 'ieron'} convertir: ${f.map((x) => `${x.nombre || 'sin nombre'} (${x.motivo})`).join('; ')}`);
  if (p) partes.push(`faltan ${p} por convertir`);
  return partes.join(' · ');
}

const fecha = (d: string) => new Date(d).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' });
const mes = (d: string) => new Date(d).toLocaleDateString('es-MX', { month: 'long', year: 'numeric' });

type Filtro = 'todas' | 'autorizadas' | 'ad' | 'marketing' | 'papelera';
type Agrupar = 'fecha' | 'paciente' | 'tratamiento';

export function GaleriaContenido() {
  const { user } = useAuth();
  const role = user?.role || '';
  const [d, setD] = useState<Resp | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filtro, setFiltro] = useState<Filtro>('todas');
  const [agrupar, setAgrupar] = useState<Agrupar>('fecha');
  const [filtroTrat, setFiltroTrat] = useState<string | null>(null);
  const [abierta, setAbierta] = useState<Item | null>(null);
  const [editando, setEditando] = useState<Item | null>(null);      // dibujo
  const [reacomodando, setReacomodando] = useState<Item | null>(null); // collage
  const [eligiendo, setEligiendo] = useState<Item | null>(null);    // menú Dibujar/Reacomodar
  const [subiendo, setSubiendo] = useState(false);                  // modal Subir archivos
  const [rawMsg, setRawMsg] = useState<string | null>(null);        // aviso de RAW convertidas
  const [rawBusy, setRawBusy] = useState(false);

  // `previo` conserva lo que acaba de pasar (p. ej. el lote de "Convertir
  // ahora") aunque la recarga ya no tenga nada que reportar.
  const load = (previo?: string | null) => {
    setD(null); setError(null);
    api<Resp>('marketing/galeria')
      .then((r) => { setD(r); setRawMsg([previo, mensajeRaw(r)].filter(Boolean).join(' · ') || null); })
      .catch((e) => setError(e.message));
  };
  useEffect(() => { load(); }, []);

  const convertirRaw = async () => {
    setRawBusy(true);
    try {
      const r = await api<{ convertidas: { id: string }[]; fallidas: RawFallida[]; pendientes: number }>(
        'marketing/galeria/convertir-raw', { method: 'POST', body: JSON.stringify({ limite: 20 }) });
      load(mensajeRaw({ rawConvertidas: r.convertidas.length, rawFallidas: r.fallidas }));
    } catch (e: any) { setRawMsg(`No se pudo convertir: ${e.message || 'error'}`); }
    finally { setRawBusy(false); }
  };

  // Acciones sobre una foto abierta: cierran el lightbox y recargan (la
  // respuesta del servidor es la verdad; no se parcha el estado local).
  const accion = async (fn: () => Promise<unknown>) => {
    try { await fn(); setAbierta(null); load(); }
    catch (e: any) { avisar({ titulo: 'No se pudo completar la acción', mensaje: textoDeError(e), tipo: 'error' }); }
  };
  const ocultar = (it: Item, oc: boolean) =>
    accion(() => api(`marketing/galeria/${it.id}/ocultar`, { method: 'POST', body: JSON.stringify({ ocultar: oc }) }));
  const compartir = (it: Item, comp: boolean) =>
    accion(() => api(`marketing/galeria/${it.id}/compartir`, { method: 'POST', body: JSON.stringify({ compartir: comp }) }));
  const eliminar = (it: Item) =>
    accion(() => api(`marketing/galeria/${it.id}`, { method: 'DELETE' }));

  const tratamientos = useMemo(() => {
    const c = new Map<string, number>();
    (d?.items || []).forEach((it) => it.tratamientos.forEach((t) => c.set(t, (c.get(t) || 0) + 1)));
    return [...c.entries()].sort((a, b) => b[1] - a[1]).slice(0, 20);
  }, [d]);

  // La papelera es un filtro más: fuera de ella, lo oculto no aparece; dentro,
  // SOLO aparece lo oculto (con su botón de Restaurar).
  const visibles = (d?.items || [])
    .filter((it) => filtro === 'papelera'
      ? it.oculta
      : !it.oculta && (filtro === 'todas' || (filtro === 'autorizadas' ? it.autorizada : filtro === 'ad' ? it.esAD : it.esMarketing)))
    .filter((it) => !filtroTrat || it.tratamientos.includes(filtroTrat));

  // Agrupación en memoria: mes / paciente (código) / tratamiento.
  const grupos = useMemo(() => {
    const g = new Map<string, Item[]>();
    for (const it of visibles) {
      const claves = agrupar === 'fecha' ? [mes(it.fecha)]
        : agrupar === 'paciente' ? [`Paciente ${it.paciente}`]
        : (it.tratamientos.length ? it.tratamientos : ['(sin tratamiento)']);
      for (const k of claves) { const a = g.get(k) || []; a.push(it); g.set(k, a); }
    }
    return [...g.entries()];
  }, [visibles, agrupar]);

  const chip = (activo: boolean): React.CSSProperties => ({
    fontSize: 12, padding: '3px 10px', fontWeight: activo ? 700 : 400,
    borderColor: activo ? 'var(--gold)' : undefined,
  });

  return (
    <div>

        {/* La advertencia ES la pantalla: se ve todo, se publica solo lo autorizado. */}
        <div style={{ background: 'var(--warning-bg, #fef3c7)', color: '#92400e', borderRadius: 10, padding: '10px 14px', fontSize: 13, lineHeight: 1.5, margin: '0 0 14px' }}>
          <strong>Aquí se ve todo el material para poder planear, pero solo lo marcado como
          "Autorizada" se puede publicar.</strong> Los nombres no aparecen: la referencia de
          paciente es un código.
        </div>

        {rawMsg && (
          <div style={{ background: '#eef2ff', color: '#3730a3', borderRadius: 10, padding: '10px 14px', fontSize: 13, lineHeight: 1.5, margin: '0 0 14px', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ flex: 1 }}>{rawMsg}</span>
            {(d?.rawPendientes || 0) > 0 && (
              <button className="btn-ghost" disabled={rawBusy} onClick={convertirRaw}>{rawBusy ? 'Convirtiendo…' : 'Convertir ahora'}</button>
            )}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', margin: '0 2px 8px' }}>
          <button className="btn-ghost" style={chip(filtro === 'todas')} onClick={() => setFiltro('todas')}>Todas</button>
          <button className="btn-ghost" style={chip(filtro === 'autorizadas')} onClick={() => setFiltro('autorizadas')}>Solo autorizadas</button>
          <button className="btn-ghost" style={chip(filtro === 'ad')} onClick={() => setFiltro('ad')}>Antes/después</button>
          <button className="btn-ghost" style={chip(filtro === 'marketing')} onClick={() => setFiltro('marketing')}>De marketing</button>
          <button className="btn-ghost" style={chip(filtro === 'papelera')} onClick={() => setFiltro('papelera')}>
            Papelera{d && d.ocultas ? ` · ${d.ocultas}` : ''}</button>
          <span className="muted" style={{ fontSize: 12, marginLeft: 10 }}>Agrupar por:</span>
          <button className="btn-ghost" style={chip(agrupar === 'fecha')} onClick={() => setAgrupar('fecha')}>Fecha</button>
          <button className="btn-ghost" style={chip(agrupar === 'paciente')} onClick={() => setAgrupar('paciente')}>Paciente</button>
          <button className="btn-ghost" style={chip(agrupar === 'tratamiento')} onClick={() => setAgrupar('tratamiento')}>Tratamiento</button>
          <button className="btn-primary" style={{ marginLeft: 'auto', fontSize: 13, padding: '6px 14px' }}
            onClick={() => setSubiendo(true)}>Subir archivos</button>
        </div>
        {filtro === 'papelera' && (
          <div className="muted" style={{ fontSize: 12, margin: '0 2px 10px', lineHeight: 1.5 }}>
            La papelera solo OCULTA de esta galería: el equipo médico sigue viendo el expediente igual en DraIA.
            Abre una foto para restaurarla o eliminarla.
          </div>
        )}
        {tratamientos.length > 0 && (
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', margin: '0 2px 14px' }}>
            {tratamientos.map(([t, nn]) => (
              <button key={t} className="btn-ghost" style={chip(filtroTrat === t)}
                onClick={() => setFiltroTrat(filtroTrat === t ? null : t)}>{t} · {nn}</button>
            ))}
          </div>
        )}

        {error && <div className="card" style={{ padding: 16, color: 'var(--warning)', background: 'var(--warning-bg)' }}>{error}</div>}
        {!d && !error && <div className="muted" style={{ padding: 40, textAlign: 'center' }}>Cargando galería…</div>}

        {d?.sinDatos && (
          <div className="card" style={{ padding: 18 }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>La galería no está conectada</div>
            <div className="muted" style={{ fontSize: 14, lineHeight: 1.6 }}>{d.motivo} {d.queHacer}</div>
          </div>
        )}

        {d && !d.sinDatos && d.items.length === 0 && (
          <div className="card" style={{ padding: 18 }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>Todavía no hay fotos</div>
            <div className="muted" style={{ fontSize: 14, lineHeight: 1.6 }}>
              Las fotos se toman y suben desde el expediente en DraIA (Fotos del paciente). En cuanto haya, aparecen aquí solas.
            </div>
          </div>
        )}

        {d && !d.sinDatos && d.items.length > 0 && (
          <>
            <div className="muted" style={{ fontSize: 12, margin: '0 2px 10px' }}>
              {visibles.length} de {d.total} en pantalla · {d.autorizadas} autorizadas · {d.conAD} antes/después
              {d.truncado ? ' · OJO: hay más de lo que se muestra (recorte del servidor)' : ''}
            </div>
            {grupos.map(([titulo, items]) => (
              <div key={titulo} style={{ marginBottom: 22 }}>
                <div style={{ fontWeight: 600, color: 'var(--gold-dark)', fontSize: 14, margin: '0 2px 8px', textTransform: 'capitalize' }}>
                  {titulo} <span className="muted" style={{ fontWeight: 400 }}>· {items.length}</span></div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
                  {items.map((it) => <Tarjeta key={titulo + it.id} it={it} onAbrir={() => setAbierta(it)} />)}
                </div>
              </div>
            ))}
          </>
        )}

      {abierta && !editando && !reacomodando && !eligiendo && (
        <Lightbox it={abierta} role={role} onCerrar={() => setAbierta(null)}
          onEditar={() => {
            // El mismo par de opciones que tienen los doctores en DraIA: si es
            // un antes/después con definición, se puede REACOMODAR; si no, dibujar.
            if (abierta.esAD && (abierta.collage?.items?.length || 0) >= 2) setEligiendo(abierta);
            else setEditando(abierta);
          }}
          onOcultar={(oc) => ocultar(abierta, oc)}
          onCompartir={(comp) => compartir(abierta, comp)}
          onEliminar={() => eliminar(abierta)} />
      )}
      {subiendo && (
        <SubirModal
          codigos={[...new Set((d?.items || []).map((x) => x.paciente))].sort()}
          sugerencias={tratamientos.map(([t]) => t)}
          onCerrar={() => setSubiendo(false)}
          onListo={() => { setSubiendo(false); load(); }} />
      )}
      {eligiendo && (
        <div onClick={() => setEligiendo(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(20,25,30,0.7)', zIndex: 70, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div onClick={(e) => e.stopPropagation()} className="card" style={{ padding: 18, width: 'min(92vw, 340px)' }}>
            <div style={{ fontWeight: 700, color: 'var(--gold-dark)', marginBottom: 12 }}>Editar antes/después</div>
            <button className="btn-primary" style={{ width: '100%', marginBottom: 8 }}
              onClick={() => { setReacomodando(eligiendo); setEligiendo(null); }}>Reacomodar el collage</button>
            <button className="btn-ghost" style={{ width: '100%', marginBottom: 8 }}
              onClick={() => { setEditando(eligiendo); setEligiendo(null); }}>Dibujar encima</button>
            <button className="btn-ghost" style={{ width: '100%', fontSize: 13 }} onClick={() => setEligiendo(null)}>Cancelar</button>
            <div className="muted" style={{ fontSize: 11, marginTop: 8 }}>Las dos guardan una copia nueva; la original no se toca.</div>
          </div>
        </div>
      )}
      {editando && (
        <Editor it={editando} onCerrar={() => setEditando(null)}
          onGuardado={() => { setEditando(null); setAbierta(null); load(); }} />
      )}
      {reacomodando && (
        <EditorAD it={reacomodando} onCerrar={() => setReacomodando(null)}
          onGuardado={() => { setReacomodando(null); setAbierta(null); load(); }} />
      )}
    </div>
  );
}

/**
 * Un HEIC (foto cruda del iPhone) no lo pinta ningún navegador salvo Safari.
 * Se baja por el proxy del mismo origen y se convierte AQUÍ con un
 * decodificador que se carga solo la primera vez que hace falta. Las
 * conversiones se recuerdan por foto mientras la pestaña viva.
 */
const NO_NATIVOS = new Set(['heic', 'tiff', 'bmp', 'dng', 'raw']);
const _convertidas = new Map<string, Promise<string>>();
export async function blobVisible(blob: Blob, formato?: string | null): Promise<Blob> {
  if (formato !== 'heic') return blob;
  const mod = await import('heic2any');
  const heic2any = (mod as unknown as { default: (o: { blob: Blob; toType: string; quality?: number }) => Promise<Blob | Blob[]> }).default;
  const out = await heic2any({ blob, toType: 'image/jpeg', quality: 0.92 });
  return Array.isArray(out) ? out[0] : out;
}
function urlConvertida(id: string, formato?: string | null): Promise<string> {
  let p = _convertidas.get(id);
  if (!p) {
    p = fetchArchivo(id).then((b) => blobVisible(b, formato)).then((b) => URL.createObjectURL(b));
    p.catch(() => _convertidas.delete(id));
    _convertidas.set(id, p);
  }
  return p;
}
/** <img> que dice POR QUÉ no se ve una foto en vez de un cuadro roto, y que
 *  convierte los formatos que el navegador no pinta. */
function ImgFoto({ it, style, onClick, onReintentar }: { it: Item; style?: React.CSSProperties; onClick?: (e: React.MouseEvent) => void; onReintentar?: () => void }) {
  const [src, setSrc] = useState<string | null>(NO_NATIVOS.has(it.formato || '') ? null : it.url);
  const [estado, setEstado] = useState<'ok' | 'convirtiendo' | 'error'>(NO_NATIVOS.has(it.formato || '') ? 'convirtiendo' : 'ok');
  const [motivo, setMotivo] = useState<string>('');
  useEffect(() => {
    let vivo = true;
    if (!NO_NATIVOS.has(it.formato || '')) { setSrc(it.url); setEstado('ok'); return; }
    if (it.formato === 'dng' || it.formato === 'raw') { setEstado('error'); setMotivo(`Es RAW (${it.formato === 'dng' ? 'DNG / ProRAW del iPhone' : 'RAW de cámara'}): ningún navegador lo muestra. Pide que la exporten como JPEG y la suban de nuevo (en el iPhone: Ajustes → Cámara → Formatos → apagar ProRAW).`); return; }
    if (it.formato !== 'heic') { setEstado('error'); setMotivo(`Formato ${it.formato?.toUpperCase()}: el navegador no lo puede mostrar. Descárgala para verla.`); return; }
    setEstado('convirtiendo');
    urlConvertida(it.id, it.formato).then((u) => { if (vivo) { setSrc(u); setEstado('ok'); } })
      .catch((e) => { if (vivo) { setEstado('error'); setMotivo(`Es HEIC (foto cruda del iPhone) y no se pudo convertir aquí: ${e instanceof Error ? e.message : 'error'}. Descárgala para verla.`); } });
    return () => { vivo = false; };
  }, [it.id, it.url, it.formato]);
  if (estado === 'convirtiendo') return <div style={{ ...style, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#bbb', fontSize: 12, padding: 8, textAlign: 'center' }}>Convirtiendo HEIC…</div>;
  if (estado === 'error' || !src) {
    return (
      <div style={{ ...style, display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'center', justifyContent: 'center', color: '#fca5a5', fontSize: 11, padding: 10, textAlign: 'center', background: '#1f1f1f' }} onClick={onClick}>
        <span>{motivo || 'No se pudo cargar la foto.'}</span>
        {onReintentar && <button className="btn-ghost" style={{ fontSize: 11 }} onClick={(e) => { e.stopPropagation(); onReintentar(); }}>Reintentar</button>}
      </div>
    );
  }
  return <img src={src} alt={it.titulo} loading="lazy" style={style} onClick={onClick}
    onError={() => { setEstado('error'); setMotivo(`No cargó (${it.formato || 'formato desconocido'}). Si la galería lleva horas abierta, la liga firmada caducó: reintenta.`); }} />;
}

function Tarjeta({ it, onAbrir }: { it: Item; onAbrir: () => void }) {
  return (
    <div className="card" onClick={onAbrir} style={{ padding: 0, overflow: 'hidden', cursor: 'zoom-in' }}>
      <div style={{ position: 'relative', aspectRatio: '1', background: '#111' }}>
        <ImgFoto it={it} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', opacity: it.oculta ? 0.45 : 1 }} />
        {it.esAD && <span style={{ position: 'absolute', right: 6, top: 6, fontSize: 10, fontWeight: 700, background: 'rgba(142,26,95,0.92)', color: '#fff', borderRadius: 8, padding: '1px 7px' }}>A/D</span>}
        {it.esMarketing && (
          <span style={{ position: 'absolute', right: 6, top: it.esAD ? 26 : 6, fontSize: 10, fontWeight: 700, borderRadius: 8, padding: '1px 7px',
            background: it.compartida ? '#d1fae5' : 'rgba(172,153,108,0.95)', color: it.compartida ? '#065f46' : '#fff',
            border: it.compartida ? '1px solid #065f46' : 'none' }}>
            {it.compartida ? 'Compartida' : 'Marketing'}</span>
        )}
        {it.oculta && <span style={{ position: 'absolute', left: 6, top: 6, fontSize: 10, fontWeight: 700, background: 'rgba(0,0,0,0.75)', color: '#fff', borderRadius: 8, padding: '1px 7px' }}>En papelera</span>}
        <span style={{ position: 'absolute', left: 6, bottom: 6, fontSize: 10, fontWeight: 700, borderRadius: 8, padding: '2px 8px',
          background: it.autorizada ? '#d1fae5' : 'rgba(255,255,255,0.94)', color: it.autorizada ? '#065f46' : '#92400e',
          border: `1px solid ${it.autorizada ? '#065f46' : '#f59e0b'}` }}>
          {it.autorizada ? 'Autorizada' : 'SIN autorizar'}</span>
      </div>
      <div style={{ padding: '8px 10px' }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--gold-dark)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.titulo}</div>
        <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>{it.paciente} · {fecha(it.fecha)}</div>
        {it.tratamientos.length > 0 && (
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 5 }}>
            {it.tratamientos.slice(0, 2).map((t) => (
              <span key={t} style={{ fontSize: 10, background: 'rgba(172,153,108,0.14)', color: 'var(--gold-dark)', border: '1px solid rgba(172,153,108,0.4)', borderRadius: 8, padding: '1px 7px' }}>{t}</span>
            ))}
            {it.tratamientos.length > 2 && <span className="muted" style={{ fontSize: 10 }}>+{it.tratamientos.length - 2}</span>}
          </div>
        )}
      </div>
    </div>
  );
}

// Botón del lightbox/editor: SIEMPRE legible sobre fondo oscuro. (Los primeros
// usaban .btn-ghost, que trae fondo claro — se veían blancos sin texto.)
const btnOscuro: React.CSSProperties = {
  background: 'rgba(255,255,255,0.08)', color: '#fff', border: '1.5px solid rgba(255,255,255,0.55)',
  borderRadius: 10, padding: '8px 18px', fontSize: 14, fontFamily: 'inherit', cursor: 'pointer',
};

function Lightbox({ it, role, onCerrar, onEditar, onOcultar, onCompartir, onEliminar }: {
  it: Item; role: string; onCerrar: () => void; onEditar: () => void;
  onOcultar: (ocultar: boolean) => void; onCompartir: (compartir: boolean) => void; onEliminar: () => void;
}) {
  // Confirmación de borrado EN el lightbox (nada de popups del navegador).
  const [confirmando, setConfirmando] = useState(false);
  // Reglas acordadas: admin borra cualquier foto; marketing solo su material.
  // Una cuenta de admin borra cualquier foto; quien no es admin (marketing, o quien tenga la galería abierta en Permisos),
  // sólo el material de marketing — la misma regla que el API (marketing-galeria-borrar).
  const puedeEliminar = role === 'admin' || it.esMarketing;
  return (
    <div onClick={onCerrar}
      style={{ position: 'fixed', inset: 0, background: 'rgba(20,25,30,0.93)', zIndex: 60,
               display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, padding: 16 }}>
      <ImgFoto it={it} onClick={(e) => e.stopPropagation()} style={{ maxWidth: '94vw', maxHeight: '66vh', borderRadius: 10, boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }} />
      <div style={{ color: '#e5e7eb', fontSize: 14, textAlign: 'center' }}>
        <strong>{it.titulo}</strong> · {it.paciente} · {fecha(it.fecha)}
        {!it.autorizada && <span style={{ display: 'inline-block', marginLeft: 8, fontSize: 12, fontWeight: 700, background: '#fef3c7', color: '#92400e', borderRadius: 8, padding: '1px 8px' }}>SIN autorizar — no publicar</span>}
        {it.esMarketing && (
          <span style={{ display: 'inline-block', marginLeft: 8, fontSize: 12, fontWeight: 700, borderRadius: 8, padding: '1px 8px',
            background: it.compartida ? '#d1fae5' : 'rgba(172,153,108,0.9)', color: it.compartida ? '#065f46' : '#fff' }}>
            {it.compartida ? 'Compartida con médicos' : 'Solo marketing (privada)'}</span>
        )}
        {it.oculta && <span style={{ display: 'inline-block', marginLeft: 8, fontSize: 12, fontWeight: 700, background: 'rgba(0,0,0,0.6)', color: '#fff', borderRadius: 8, padding: '1px 8px' }}>En papelera</span>}
        {it.tratamientos.length > 0 && <div style={{ fontSize: 12, opacity: 0.8, marginTop: 3 }}>{it.tratamientos.join(' · ')}</div>}
      </div>
      {confirmando ? (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
          <span style={{ color: '#fbbf24', fontSize: 13, fontWeight: 700 }}>¿Eliminar esta foto para todos?</span>
          <button style={{ ...btnOscuro, background: '#ef4444', borderColor: '#ef4444', fontWeight: 700 }} onClick={onEliminar}>Sí, eliminar</button>
          <button style={btnOscuro} onClick={() => setConfirmando(false)}>Cancelar</button>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }} onClick={(e) => e.stopPropagation()}>
          <button style={btnOscuro} onClick={onEditar}>Editar (copia)</button>
          <button style={btnOscuro} onClick={() => { descargar(it).catch((e) => avisar({ titulo: 'No se pudo descargar la foto', mensaje: textoDeError(e, 'No se pudo descargar la foto.'), tipo: 'error' })); }}>Descargar</button>
          {it.esMarketing && (
            <button style={btnOscuro} onClick={() => onCompartir(!it.compartida)}>
              {it.compartida ? 'Dejar de compartir' : 'Compartir con médicos'}</button>
          )}
          <button style={btnOscuro} onClick={() => onOcultar(!it.oculta)} title="Solo la oculta de esta galería; el equipo médico la sigue viendo en DraIA">
            {it.oculta ? 'Restaurar' : 'Ocultar (papelera)'}</button>
          {puedeEliminar && (
            <button style={{ ...btnOscuro, borderColor: 'rgba(239,68,68,0.8)', color: '#fca5a5' }} onClick={() => setConfirmando(true)}>Eliminar</button>
          )}
          <button style={btnOscuro} onClick={onCerrar}>Cerrar</button>
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ editor
// Mismas herramientas que el expediente de DraIA (pluma, flecha, círculo,
// texto). Guarda una COPIA "(marketing)" vía POST galeria/anotada.
type Herr = 'pluma' | 'flecha' | 'circulo' | 'texto';
interface Trazo { tool: Herr; color: string; w: number; pts: { x: number; y: number }[]; text?: string }
const COLORES = ['#8E1A5F', '#AC996C', '#ef4444', '#ffffff'];

function Editor({ it, onCerrar, onGuardado }: { it: Item; onCerrar: () => void; onGuardado: () => void }) {
  const cvRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const trazosRef = useRef<Trazo[]>([]);
  const actualRef = useRef<Trazo | null>(null);
  const [herr, setHerr] = useState<Herr>('pluma');
  const [color, setColor] = useState(COLORES[0]);
  const [grosor, setGrosor] = useState(4);
  const [texto, setTexto] = useState<{ x: number; y: number } | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [listo, setListo] = useState(false);

  useEffect(() => {
    let objUrl = '';
    fetchArchivo(it.id).then((blob) => {
      objUrl = URL.createObjectURL(blob); // mismo origen: el canvas no se mancha
      const im = new Image();
      im.onload = () => { imgRef.current = im; const cv = cvRef.current; if (cv) { cv.width = im.width; cv.height = im.height; } setListo(true); repinta(); };
      im.onerror = () => setMsg('No se pudo cargar la imagen para editar.');
      im.src = objUrl;
    }).catch((e) => setMsg(e.message));
    return () => { if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [it.id]);

  function pinta(ctx: CanvasRenderingContext2D, t: Trazo) {
    ctx.strokeStyle = t.color; ctx.fillStyle = t.color; ctx.lineWidth = t.w; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
    const p = t.pts;
    if (!p.length) return;
    if (t.tool === 'pluma') {
      ctx.beginPath(); ctx.moveTo(p[0].x, p[0].y);
      for (let i = 1; i < p.length; i++) ctx.lineTo(p[i].x, p[i].y);
      ctx.stroke();
    } else if (t.tool === 'flecha' && p.length >= 2) {
      const a = p[0], b = p[p.length - 1];
      ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke();
      const ang = Math.atan2(b.y - a.y, b.x - a.x), L = Math.max(14, t.w * 4);
      ctx.beginPath();
      ctx.moveTo(b.x, b.y); ctx.lineTo(b.x - L * Math.cos(ang - 0.45), b.y - L * Math.sin(ang - 0.45));
      ctx.moveTo(b.x, b.y); ctx.lineTo(b.x - L * Math.cos(ang + 0.45), b.y - L * Math.sin(ang + 0.45));
      ctx.stroke();
    } else if (t.tool === 'circulo' && p.length >= 2) {
      const a = p[0], b = p[p.length - 1];
      ctx.beginPath();
      ctx.ellipse((a.x + b.x) / 2, (a.y + b.y) / 2, Math.abs(b.x - a.x) / 2 || 1, Math.abs(b.y - a.y) / 2 || 1, 0, 0, Math.PI * 2);
      ctx.stroke();
    } else if (t.tool === 'texto' && t.text) {
      ctx.font = `700 ${t.w * 9}px Montserrat, sans-serif`;
      ctx.fillText(t.text, p[0].x, p[0].y);
    }
  }
  function repinta() {
    const cv = cvRef.current, im = imgRef.current;
    if (!cv || !im) return;
    const ctx = cv.getContext('2d')!;
    ctx.clearRect(0, 0, cv.width, cv.height);
    ctx.drawImage(im, 0, 0);
    trazosRef.current.forEach((t) => pinta(ctx, t));
    if (actualRef.current) pinta(ctx, actualRef.current);
  }
  const coords = (e: React.PointerEvent) => {
    const cv = cvRef.current!;
    const r = cv.getBoundingClientRect();
    return { x: (e.clientX - r.left) * (cv.width / r.width), y: (e.clientY - r.top) * (cv.height / r.height) };
  };
  const escala = () => { const cv = cvRef.current!; return cv.width / cv.getBoundingClientRect().width; };

  const down = (e: React.PointerEvent) => {
    if (!listo) return;
    const c = coords(e);
    if (herr === 'texto') { setTexto(c); return; }
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    actualRef.current = { tool: herr, color, w: grosor * escala(), pts: [c] };
  };
  const move = (e: React.PointerEvent) => { if (actualRef.current) { actualRef.current.pts.push(coords(e)); repinta(); } };
  const up = () => {
    if (actualRef.current && actualRef.current.pts.length > 1) trazosRef.current.push(actualRef.current);
    actualRef.current = null; repinta();
  };
  const commitTexto = (valor: string) => {
    if (texto && valor.trim()) {
      trazosRef.current.push({ tool: 'texto', color, w: grosor * escala(), pts: [texto], text: valor.trim() });
    }
    setTexto(null); repinta();
  };

  const guardar = async () => {
    const cv = cvRef.current;
    if (!cv) return;
    setBusy(true); setMsg(null);
    try {
      const dataUrl = cv.toDataURL('image/jpeg', 0.92);
      await api('marketing/galeria/anotada', { method: 'POST', body: JSON.stringify({ sourceId: it.id, imagen: dataUrl }) });
      onGuardado();
    } catch (e: any) {
      setMsg(/tainted|insecure|SecurityError/i.test(e.message) ? 'El navegador bloqueó la exportación (CORS del storage). Descarga la foto y edítala localmente mientras lo revisamos.' : e.message);
      setBusy(false);
    }
  };

  const toolBtn = (t: Herr): React.CSSProperties => ({
    ...btnOscuro, padding: '6px 12px', fontSize: 13,
    background: herr === t ? 'var(--gold, #AC996C)' : 'rgba(255,255,255,0.08)',
    borderColor: herr === t ? 'var(--gold, #AC996C)' : 'rgba(255,255,255,0.4)',
  });

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(20,25,30,0.95)', zIndex: 70,
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, padding: 12 }}>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
        <button style={toolBtn('pluma')} onClick={() => setHerr('pluma')}>Pluma</button>
        <button style={toolBtn('flecha')} onClick={() => setHerr('flecha')}>Flecha</button>
        <button style={toolBtn('circulo')} onClick={() => setHerr('circulo')}>Círculo</button>
        <button style={toolBtn('texto')} onClick={() => setHerr('texto')}>Texto</button>
        <span style={{ width: 8 }} />
        {COLORES.map((c) => (
          <span key={c} onClick={() => setColor(c)}
            style={{ width: 22, height: 22, borderRadius: '50%', background: c, cursor: 'pointer', display: 'inline-block',
                     border: `2.5px solid ${c === color ? '#fff' : 'rgba(255,255,255,0.25)'}` }} />
        ))}
        <input type="range" min={2} max={14} value={grosor} onChange={(e) => setGrosor(Number(e.target.value))} style={{ width: 90 }} title="Grosor / tamaño de texto" />
        <button style={{ ...btnOscuro, padding: '6px 12px', fontSize: 13 }} onClick={() => { trazosRef.current.pop(); repinta(); }}>Deshacer</button>
        <button style={{ ...btnOscuro, padding: '6px 12px', fontSize: 13 }} onClick={() => { trazosRef.current = []; repinta(); }}>Limpiar</button>
      </div>
      <div style={{ position: 'relative', lineHeight: 0 }}>
        <canvas ref={cvRef} onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up}
          style={{ maxWidth: '94vw', maxHeight: '68vh', borderRadius: 10, background: '#000', touchAction: 'none', cursor: 'crosshair' }} />
        {texto && cvRef.current && (
          <input autoFocus placeholder="Escribe y Enter"
            onKeyDown={(e) => { if (e.key === 'Enter') commitTexto((e.target as HTMLInputElement).value); if (e.key === 'Escape') setTexto(null); }}
            onBlur={(e) => commitTexto(e.target.value)}
            style={{ position: 'absolute',
                     left: (texto.x / cvRef.current.width) * cvRef.current.getBoundingClientRect().width,
                     top: (texto.y / cvRef.current.height) * cvRef.current.getBoundingClientRect().height,
                     padding: '4px 8px', border: `2px solid ${color}`, borderRadius: 6, fontFamily: 'inherit', fontSize: 14 }} />
        )}
      </div>
      {!listo && !msg && <div style={{ color: '#e5e7eb', fontSize: 13 }}>Cargando la foto en alta resolución…</div>}
      {msg && <div style={{ color: '#fbbf24', fontSize: 13, maxWidth: 560, textAlign: 'center' }}>{msg}</div>}
      <div style={{ display: 'flex', gap: 10 }}>
        <button style={{ ...btnOscuro, background: 'var(--gold, #AC996C)', borderColor: 'var(--gold, #AC996C)', fontWeight: 700 }}
          onClick={guardar} disabled={busy || !listo}>{busy ? 'Guardando…' : 'Guardar copia (marketing)'}</button>
        <button style={btnOscuro} onClick={onCerrar} disabled={busy}>Cancelar</button>
      </div>
    </div>
  );
}

// ------------------------------------------------------------- reacomodo A/D
// El MISMO módulo que tienen los doctores en DraIA, portado: cada casilla con
// su arrastre (mover), rueda/pellizco (zoom), giro con dos dedos o botones, y
// acomodo horizontal/vertical/cuadrícula. Guarda una copia nueva que sigue
// siendo un antes/después re-editable (viaja su definición de collage).
interface CeldaT { s: number; x: number; y: number; r: number }

export function EditorAD({ it, onCerrar, onGuardado }: { it: Item; onCerrar: () => void; onGuardado: () => void }) {
  const [layout, setLayout] = useState<'h' | 'v' | 'g'>((it.collage?.layout === 'v' || it.collage?.layout === 'g' ? it.collage.layout : 'h') as 'h' | 'v' | 'g');
  const [fotos, setFotos] = useState<{ id: string; url: string; img: HTMLImageElement }[] | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const cellsRef = useRef<Record<string, CeldaT>>({});
  const gridRef = useRef<HTMLDivElement>(null);
  const escalado = useRef(false);

  useEffect(() => {
    const items = it.collage?.items || [];
    let vivos: { id: string; url: string; img: HTMLImageElement }[] = [];
    let cancelado = false;
    (async () => {
      for (const c of items.slice(0, 4)) {
        try {
          const crudo = await fetchArchivo(c.document_id);
          const blob = await blobVisible(crudo, /hei[cf]/i.test(crudo.type) ? 'heic' : null);
          const url = URL.createObjectURL(blob);
          const img = await new Promise<HTMLImageElement>((res, rej) => {
            const im = new Image(); im.onload = () => res(im); im.onerror = rej; im.src = url;
          });
          vivos.push({ id: c.document_id, url, img });
          cellsRef.current[c.document_id] = { s: Number(c.s) || 1, x: Number(c.x) || 0, y: Number(c.y) || 0, r: Number(c.r) || 0 };
        } catch { /* la foto original ya no está: se abre con las que queden */ }
      }
      if (cancelado) return;
      if (vivos.length < 2) { setMsg('No se puede reacomodar: las fotos originales de este antes/después ya no están completas en el expediente.'); return; }
      if (vivos.length < items.length) setMsg('Ojo: una foto original ya no existe; se abre con las que quedan.');
      setFotos(vivos);
    })();
    return () => { cancelado = true; vivos.forEach((v) => URL.revokeObjectURL(v.url)); };
  }, [it.id]);

  // Los x/y guardados están en píxeles de la pantalla donde se creó: se
  // reescalan contra la altura de casilla original (cellH) — la misma regla
  // que DraIA usa para que el acomodo se vea IGUAL en cualquier pantalla.
  useEffect(() => {
    if (!fotos || escalado.current) return;
    requestAnimationFrame(() => {
      const cell = gridRef.current?.querySelector('[data-cell]') as HTMLElement | null;
      const guardadoH = Number(it.collage?.cellH) || 0;
      if (cell && guardadoH > 0) {
        const f = cell.clientHeight / guardadoH;
        if (f && f !== 1) Object.values(cellsRef.current).forEach((c) => { c.x *= f; c.y *= f; });
      }
      escalado.current = true;
      fotos.forEach((v) => pinta(v.id));
    });
  }, [fotos]);

  const pinta = (id: string) => {
    const img = gridRef.current?.querySelector(`[data-img="${id}"]`) as HTMLElement | null;
    const c = cellsRef.current[id];
    if (img && c) img.style.transform = `translate(-50%,-50%) translate(${c.x}px,${c.y}px) scale(${c.s}) rotate(${c.r}deg)`;
  };

  // Gestos por casilla (portados de DraIA): 1 dedo mueve, 2 dedos hacen zoom y
  // giran, la rueda hace zoom. Refs para no re-renderizar en cada movimiento.
  const gestos = useRef<Record<string, { ptrs: Map<number, { x: number; y: number; sx?: number; sy?: number }>; base: CeldaT | null }>>({});
  const g = (id: string) => (gestos.current[id] ||= { ptrs: new Map(), base: null });

  const down = (id: string) => (e: React.PointerEvent) => {
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    g(id).ptrs.set(e.pointerId, { x: e.clientX, y: e.clientY });
    g(id).base = { ...cellsRef.current[id] };
  };
  const move = (id: string) => (e: React.PointerEvent) => {
    const st = g(id);
    if (!st.ptrs.has(e.pointerId) || !st.base) return;
    const prev = st.ptrs.get(e.pointerId)!;
    st.ptrs.set(e.pointerId, { x: e.clientX, y: e.clientY, sx: prev.sx ?? prev.x, sy: prev.sy ?? prev.y });
    const now = [...st.ptrs.values()];
    const c = cellsRef.current[id];
    if (now.length === 1) {
      c.x = st.base.x + (now[0].x - (now[0].sx ?? now[0].x));
      c.y = st.base.y + (now[0].y - (now[0].sy ?? now[0].y));
    } else if (now.length >= 2) {
      const d0 = Math.hypot((now[0].sx ?? now[0].x) - (now[1].sx ?? now[1].x), (now[0].sy ?? now[0].y) - (now[1].sy ?? now[1].y));
      const d1 = Math.hypot(now[0].x - now[1].x, now[0].y - now[1].y);
      if (d0 > 0) c.s = Math.min(8, Math.max(0.2, st.base.s * (d1 / d0)));
      const a0 = Math.atan2((now[1].sy ?? now[1].y) - (now[0].sy ?? now[0].y), (now[1].sx ?? now[1].x) - (now[0].sx ?? now[0].x));
      const a1 = Math.atan2(now[1].y - now[0].y, now[1].x - now[0].x);
      c.r = st.base.r + ((a1 - a0) * 180) / Math.PI;
    }
    pinta(id);
  };
  const up = (id: string) => (e: React.PointerEvent) => {
    g(id).ptrs.delete(e.pointerId);
    g(id).base = { ...cellsRef.current[id] };
  };
  const rueda = (id: string) => (e: React.WheelEvent) => {
    const c = cellsRef.current[id];
    c.s = Math.min(8, Math.max(0.2, c.s * (e.deltaY < 0 ? 1.1 : 0.9)));
    pinta(id);
  };
  const rota = (id: string, deg: number) => { cellsRef.current[id].r = (cellsRef.current[id].r + deg) % 360; pinta(id); };
  const reset = (id: string) => { cellsRef.current[id] = { s: 1, x: 0, y: 0, r: 0 }; pinta(id); };

  const guardar = async () => {
    if (!fotos) return;
    setBusy(true); setMsg(null);
    try {
      // Rasterizado idéntico al de DraIA: casillas de 900px, transformaciones
      // escaladas de píxeles de pantalla a píxeles de casilla.
      const CELL = 900;
      const cols = layout === 'h' ? fotos.length : layout === 'v' ? 1 : 2;
      const rows = layout === 'h' ? 1 : layout === 'v' ? fotos.length : Math.ceil(fotos.length / 2);
      const canvas = document.createElement('canvas');
      canvas.width = cols * CELL; canvas.height = rows * CELL;
      const ctx = canvas.getContext('2d')!;
      ctx.fillStyle = '#111'; ctx.fillRect(0, 0, canvas.width, canvas.height);
      const cellEl = gridRef.current?.querySelector('[data-cell]') as HTMLElement | null;
      const cellH = cellEl?.clientHeight || CELL;
      const k = CELL / cellH;
      fotos.forEach((v, i) => {
        const c = cellsRef.current[v.id];
        const cx = (i % cols) * CELL + CELL / 2, cy = Math.floor(i / cols) * CELL + CELL / 2;
        ctx.save();
        ctx.beginPath(); ctx.rect((i % cols) * CELL, Math.floor(i / cols) * CELL, CELL, CELL); ctx.clip();
        ctx.translate(cx + c.x * k, cy + c.y * k);
        ctx.rotate((c.r * Math.PI) / 180);
        const w = v.img.width * (CELL / v.img.height);
        ctx.scale(c.s, c.s);
        ctx.drawImage(v.img, -w / 2, -CELL / 2, w, CELL);
        ctx.restore();
      });
      const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
      await api('marketing/galeria/anotada', {
        method: 'POST',
        body: JSON.stringify({
          sourceId: it.id, imagen: dataUrl,
          titulo: `${it.titulo} (marketing)`.slice(0, 200),
          collage: { layout, cellH, items: fotos.map((v) => ({ document_id: v.id, ...cellsRef.current[v.id] })) },
        }),
      });
      onGuardado();
    } catch (e: any) { setMsg(e.message); setBusy(false); }
  };

  const gridCss: React.CSSProperties = layout === 'h'
    ? { gridTemplateColumns: `repeat(${fotos?.length || 1}, 1fr)` }
    : layout === 'v' ? { gridTemplateRows: `repeat(${fotos?.length || 1}, 1fr)`, gridTemplateColumns: '1fr' }
    : { gridTemplateColumns: 'repeat(2, 1fr)' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(20,25,30,0.95)', zIndex: 70,
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, padding: 12 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center', color: '#e5e7eb', fontSize: 13 }}>
        <span>Acomodo:</span>
        {(['h', 'v', 'g'] as const).map((k2) => (
          <button key={k2} onClick={() => setLayout(k2)}
            style={{ ...btnOscuro, padding: '5px 12px', fontSize: 13,
                     background: layout === k2 ? 'var(--gold, #AC996C)' : 'rgba(255,255,255,0.08)',
                     borderColor: layout === k2 ? 'var(--gold, #AC996C)' : 'rgba(255,255,255,0.4)' }}>
            {k2 === 'h' ? 'Horizontal' : k2 === 'v' ? 'Vertical' : 'Cuadrícula'}</button>
        ))}
        <span className="muted" style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)' }}>arrastra para mover · rueda/pellizco para zoom · dos dedos giran</span>
      </div>
      {!fotos && !msg && <div style={{ color: '#e5e7eb', fontSize: 13 }}>Cargando las fotos originales en alta resolución…</div>}
      {msg && <div style={{ color: '#fbbf24', fontSize: 13, maxWidth: 560, textAlign: 'center' }}>{msg}</div>}
      {fotos && (
        <div ref={gridRef} style={{ display: 'grid', ...gridCss, gap: 6, background: '#111', borderRadius: 10, padding: 6,
                                     height: 'min(58vh, 520px)', width: 'min(94vw, 960px)' }}>
          {fotos.map((v) => (
            <div key={v.id} data-cell={v.id}
              onPointerDown={down(v.id)} onPointerMove={move(v.id)} onPointerUp={up(v.id)} onPointerCancel={up(v.id)}
              onWheel={rueda(v.id)}
              style={{ position: 'relative', overflow: 'hidden', borderRadius: 6, background: '#000', touchAction: 'none', minHeight: 0 }}>
              <img src={v.url} draggable={false} data-img={v.id} alt=""
                style={{ position: 'absolute', left: '50%', top: '50%', maxWidth: 'none', height: '100%',
                         transform: 'translate(-50%,-50%)', transformOrigin: 'center', userSelect: 'none' }} />
              <div onPointerDown={(e) => e.stopPropagation()} style={{ position: 'absolute', right: 4, top: 4, display: 'flex', gap: 3, zIndex: 2 }}>
                {[['⟲', () => rota(v.id, -15), 'Rotar a la izquierda'], ['⟳', () => rota(v.id, 15), 'Rotar a la derecha'], ['⌾', () => reset(v.id), 'Restablecer']].map(([lbl, fn, tt], i2) => (
                  <button key={i2} title={tt as string} onClick={fn as () => void}
                    style={{ cursor: 'pointer', border: 'none', background: 'rgba(255,255,255,0.88)', borderRadius: 6, padding: '3px 8px', fontSize: 13, lineHeight: 1 }}>{lbl as string}</button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', gap: 10 }}>
        <button style={{ ...btnOscuro, background: 'var(--gold, #AC996C)', borderColor: 'var(--gold, #AC996C)', fontWeight: 700 }}
          onClick={guardar} disabled={busy || !fotos}>{busy ? 'Guardando…' : 'Guardar copia (marketing)'}</button>
        <button style={btnOscuro} onClick={onCerrar} disabled={busy}>Cancelar</button>
      </div>
    </div>
  );
}

// ------------------------------------------------------------ subir archivos
// Sube material propio de Marketing al expediente de un paciente identificado
// por su CÓDIGO (los 8 del ID que titulan las fotos) — nunca por nombre. Lo
// subido nace PRIVADO de marketing; se comparte con los médicos foto por foto.
function SubirModal({ codigos, sugerencias, onCerrar, onListo }: {
  codigos: string[]; sugerencias: string[]; onCerrar: () => void; onListo: () => void;
}) {
  const [files, setFiles] = useState<File[]>([]);
  const [codigo, setCodigo] = useState('');
  const [titulo, setTitulo] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [tagTxt, setTagTxt] = useState('');
  const [busy, setBusy] = useState(false);
  const [hecho, setHecho] = useState(0);
  const [msg, setMsg] = useState<string | null>(null);

  const agregarTag = (t: string) => {
    const v = t.trim();
    if (v && !tags.includes(v)) setTags((prev) => [...prev, v]);
    setTagTxt('');
  };

  // JPG/PNG van directo; cualquier otro formato que el navegador SÍ sepa
  // pintar (p. ej. HEIC en Safari) se convierte a JPEG vía canvas. Si tampoco,
  // el error lo dice claro en vez de fallar en silencio.
  const comoDataUrl = (f: File) => new Promise<string>((res, rej) => {
    if (f.type === 'image/jpeg' || f.type === 'image/png') {
      const r = new FileReader();
      r.onload = () => res(r.result as string);
      r.onerror = () => rej(new Error(`No se pudo leer ${f.name}.`));
      r.readAsDataURL(f);
      return;
    }
    const url = URL.createObjectURL(f);
    const im = new Image();
    im.onload = () => {
      try {
        const cv = document.createElement('canvas');
        cv.width = im.naturalWidth; cv.height = im.naturalHeight;
        cv.getContext('2d')!.drawImage(im, 0, 0);
        res(cv.toDataURL('image/jpeg', 0.92));
      } catch { rej(new Error(`${f.name}: formato no soportado — usa JPG o PNG.`)); }
      finally { URL.revokeObjectURL(url); }
    };
    im.onerror = () => { URL.revokeObjectURL(url); rej(new Error(`${f.name}: formato no soportado — usa JPG o PNG.`)); };
    im.src = url;
  });

  const subir = async () => {
    if (!files.length) { setMsg('Elige al menos un archivo (JPG o PNG).'); return; }
    if (codigo.replace(/[^0-9a-fA-F]/g, '').length < 8) { setMsg('Falta el código de paciente: los 8 caracteres que ves en las fotos (ej. A0CBDFCD).'); return; }
    setBusy(true); setMsg(null); setHecho(0);
    try {
      for (let i = 0; i < files.length; i++) {
        const dataUrl = await comoDataUrl(files[i]);
        await api('marketing/galeria/subir', {
          method: 'POST',
          body: JSON.stringify({
            codigoPaciente: codigo,
            tratamientos: tags,
            titulo: titulo.trim() ? (files.length > 1 ? `${titulo.trim()} ${i + 1}` : titulo.trim()) : undefined,
            imagen: dataUrl,
          }),
        });
        setHecho(i + 1);
      }
      onListo();
    } catch (e: any) { setMsg(e.message); setBusy(false); }
  };

  const inputCss: React.CSSProperties = {
    width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid rgba(172,153,108,0.5)',
    fontFamily: 'inherit', fontSize: 14, boxSizing: 'border-box',
  };

  return (
    <div onClick={busy ? undefined : onCerrar}
      style={{ position: 'fixed', inset: 0, background: 'rgba(20,25,30,0.7)', zIndex: 70, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} className="card"
        style={{ padding: 18, width: 'min(94vw, 440px)', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ fontWeight: 700, color: 'var(--gold-dark)', marginBottom: 10 }}>Subir archivos a la galería</div>

        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Archivos (JPG o PNG)</label>
        <input type="file" accept="image/jpeg,image/png" multiple disabled={busy}
          onChange={(e) => setFiles([...(e.target.files || [])])} style={{ ...inputCss, padding: 6 }} />
        {files.length > 0 && <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>{files.length} archivo{files.length > 1 ? 's' : ''} elegido{files.length > 1 ? 's' : ''}</div>}

        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, margin: '12px 0 4px' }}>Código de paciente</label>
        <input list="galeria-codigos" value={codigo} disabled={busy} placeholder="Ej. A0CBDFCD"
          onChange={(e) => setCodigo(e.target.value.toUpperCase())} style={{ ...inputCss, textTransform: 'uppercase' }} />
        <datalist id="galeria-codigos">{codigos.map((c) => <option key={c} value={c} />)}</datalist>
        <div className="muted" style={{ fontSize: 11, marginTop: 3, lineHeight: 1.4 }}>
          Es el código de 8 caracteres que titula sus fotos (aparece en cada tarjeta de la galería).
        </div>

        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, margin: '12px 0 4px' }}>Título (opcional)</label>
        <input value={titulo} disabled={busy} placeholder="Si se deja vacío, se usa el código"
          onChange={(e) => setTitulo(e.target.value)} style={inputCss} />

        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, margin: '12px 0 4px' }}>Tratamientos / etiquetas (opcional)</label>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: tags.length ? 6 : 0 }}>
          {tags.map((t) => (
            <span key={t} onClick={() => !busy && setTags(tags.filter((x) => x !== t))}
              style={{ fontSize: 12, background: 'rgba(172,153,108,0.14)', color: 'var(--gold-dark)', border: '1px solid rgba(172,153,108,0.4)', borderRadius: 8, padding: '2px 8px', cursor: 'pointer' }}
              title="Quitar">{t} ✕</span>
          ))}
        </div>
        <input value={tagTxt} disabled={busy} placeholder="Escribe y Enter para agregar"
          onChange={(e) => setTagTxt(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); agregarTag(tagTxt); } }}
          onBlur={() => agregarTag(tagTxt)} style={inputCss} />
        {sugerencias.length > 0 && (
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 6 }}>
            {sugerencias.slice(0, 8).filter((s) => !tags.includes(s)).map((s) => (
              <span key={s} onClick={() => !busy && agregarTag(s)} className="muted"
                style={{ fontSize: 11, border: '1px dashed rgba(172,153,108,0.5)', borderRadius: 8, padding: '1px 7px', cursor: 'pointer' }}>+ {s}</span>
            ))}
          </div>
        )}

        <div className="muted" style={{ fontSize: 11, margin: '12px 0', lineHeight: 1.5 }}>
          Lo subido queda como material <strong>privado de marketing</strong>: el equipo médico no lo ve en DraIA
          hasta que lo compartas (botón "Compartir con médicos" al abrir la foto).
        </div>

        {msg && <div style={{ color: '#92400e', background: '#fef3c7', borderRadius: 8, padding: '8px 10px', fontSize: 13, marginBottom: 10 }}>{msg}</div>}
        {busy && <div className="muted" style={{ fontSize: 13, marginBottom: 8 }}>Subiendo… {hecho} de {files.length}</div>}

        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn-primary" style={{ flex: 1 }} onClick={subir} disabled={busy}>
            {busy ? 'Subiendo…' : 'Subir'}</button>
          <button className="btn-ghost" onClick={onCerrar} disabled={busy}>Cancelar</button>
        </div>
      </div>
    </div>
  );
}
